import React, { useState, useEffect, useRef } from 'react';
import { 
  Paper, 
  Group, 
  Stack, 
  Text, 
  ActionIcon, 
  TextInput,
  Button,
  Box,
  Divider,
  Modal
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { IconPencil, IconCheck, IconX, IconMapPin, IconPhoto } from '@tabler/icons-react';
import { ImageDetails } from '../../../../../types/Image';
import { parseExifDate } from '../../../../../utils/dateUtils';
import { EventDetailsForm } from '../../../../shared/EventDetailsForm';
import { EventData, DateFormat } from '../../../../../types/Event';
import { ShoeboxLocation } from '../../../../../types/Location';

interface EventSectionProps {
  // metadata: ImageDetails;
  event?: EventData; // Optional event data if the image is part of an event
  onEventUpdate?: (updatedEventData: Partial<EventData>) => void;
}

export const EventSection: React.FC<EventSectionProps> = ({ 
  event,
  onEventUpdate
}) => {
  const [modalOpened, setModalOpened] = useState(false);
  const [eventTitle, setEventTitle] = useState(event?.title || '');
  const [dateValue, setDateValue] = useState<Date | null>(null);
  const [currentEvent, setCurrentEvent] = useState<EventData | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const storageKeyRef = useRef<string>(event ? `image-inspector-event-${event.key}` : 'image-inspector-new-event');
  
  // Initialize date value from event data
  useEffect(() => {
    if (event?.date) {
      // If event.date is already a Date object, use it directly
      // Otherwise, try to parse it
      setDateValue(event.date instanceof Date ? event.date : new Date(event.date));
    }
  }, [event?.date]);
  
  // Initialize current event from localStorage or props
  useEffect(() => {
    if (event && event.key) {
      // Update storage key reference if event changes
      storageKeyRef.current = `image-inspector-event-${event.key}`;
      
      // Try to load saved event data from localStorage
      try {
        const savedEventData = localStorage.getItem(storageKeyRef.current);
        if (savedEventData) {
          const parsedData = JSON.parse(savedEventData);
          // Verify this is for the current event
          if (parsedData && parsedData.key === event.key) {
            console.log('[EventSection] Loaded saved event data from localStorage');
            setCurrentEvent(parsedData);
            return;
          }
        }
      } catch (e) {
        console.error('[EventSection] Failed to parse saved event data:', e);
      }
      
      // If no localStorage data or error, use the prop
      setCurrentEvent(event);
    } else {
      // For new events or events without a key
      storageKeyRef.current = 'image-inspector-new-event-' + Math.random().toString(36).substring(2, 8);
      setCurrentEvent(null);
    }
    
    setHasChanges(false);
  }, [event?.key]);

  // Function to clean up localStorage when modal is closed
  const cleanupStorage = () => {
    try {
      localStorage.removeItem(storageKeyRef.current);
      console.log('[EventSection] Cleaned up localStorage for storage key:', storageKeyRef.current);
    } catch (e) {
      console.error('[EventSection] Error cleaning up localStorage:', e);
    }
  };
  
  // Handle local updates from the form
  const handleEventUpdate = (updatedEventData: Partial<EventData>) => {
    console.log('[EventSection] Received partial update:', updatedEventData);
    
    // Get the base event data to update
    const baseEvent = currentEvent || event || getEventData();
    
    // Ensure the event has a key
    const eventKey = updatedEventData.key || baseEvent.key;
    
    // Create a complete updated event by merging
    const updatedEvent: EventData = {
      ...baseEvent,
      ...updatedEventData,
      key: eventKey // Ensure key is never undefined
    };
    
    // Save to localStorage
    try {
      localStorage.setItem(storageKeyRef.current, JSON.stringify(updatedEvent));
      console.log('[EventSection] Saved event data to localStorage');
    } catch (e) {
      console.error('[EventSection] Failed to save to localStorage:', e);
    }
    
    // Update local state
    setCurrentEvent(updatedEvent);
    setHasChanges(true);
  };
  
  // Handle save button click
  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Get the latest data
      const finalEventData = currentEvent || event;
      
      if (finalEventData && onEventUpdate) {
        // Call the onEventUpdate callback
        onEventUpdate(finalEventData);
        console.log('[EventSection] Event updated successfully:', finalEventData);
      }
      
      // Clean up localStorage
      cleanupStorage();
      
      // Close the modal
      setModalOpened(false);
      setHasChanges(false);
    } catch (error) {
      console.error('[EventSection] Failed to update event:', error);
    } finally {
      setIsSaving(false);
    }
  };

  // Format date for display (only show date, not time)
  const formatEventDate = (dateString: string): string => {
    const parsedDate = parseExifDate(dateString);
    if (!parsedDate) return 'Invalid date';
    
    return parsedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Use the provided event data or create a default empty event
  const getEventData = (): EventData => {
    if (event) return event;
    
    // Create a new default event with a guaranteed key
    return {
      key: 'new-event-' + Math.random().toString(36).substring(2, 8),
      title: '',
      images: [],
      dateFormat: DateFormat.DAY,
      keywords: []
    };
  };

  // If no event data is available, show an "Add to Event" button
  if (!event) {
    return (
      <Paper withBorder radius="md" bg="gray.1">
        <Group justify="space-between" align="end" pl={15} px={5} pt={1} pb={1}>
          <Text fw={700} size="xs" tt="uppercase">Event</Text>
        </Group>
        <Divider mt={0} mb="md"/>

        {/* Event Details Modal */}
        <Modal 
          opened={modalOpened} 
          onClose={() => {
            // Clean up localStorage when modal is closed without saving
            cleanupStorage();
            setModalOpened(false);
          }}
          title="Add to Event"
          size="lg"
          centered
        >
          <Box style={{ position: 'relative' }}>
            {/* Event details form */}
            <EventDetailsForm
              eventData={currentEvent || getEventData()}
              onEventUpdate={handleEventUpdate}
            />
            
            {/* Save button */}
            <Button
              variant="filled"
              mt="xl"
              fullWidth
              onClick={handleSave}
              disabled={isSaving}
            >
              Add to Event
            </Button>
          </Box>
        </Modal>
        
        {/* Add to Event Button */}
        <Box px="md" pb="md" ta="center">
          <Text size="sm" c="dimmed" mb="md">
            This image is not part of any event
          </Text>
          <Button
            variant="outline"
            leftSection={<IconPencil size={16} />}
            onClick={() => setModalOpened(true)}
          >
            Add to Event
          </Button>
        </Box>
      </Paper>
    );
  }

  // If event data is available, show the event details
  return (
    <Paper withBorder radius="md" bg="gray.1">
      <Group justify="space-between" align="end" pl={15} px={5} pt={1} pb={1}>
        <Text fw={700} size="xs" tt="uppercase">Event</Text>
        <ActionIcon variant="subtle" color="gray" onClick={() => setModalOpened(true)}>
          <IconPencil size={16}/>
        </ActionIcon>
      </Group>
      <Divider mt={0} mb="md"/>

      {/* Event Details Modal */}
      <Modal 
        opened={modalOpened} 
        onClose={() => {
          // Clean up localStorage when modal is closed without saving
          cleanupStorage();
          setModalOpened(false);
        }}
        title="Edit Event Details"
        size="lg"
        centered
      >
        <Box style={{ position: 'relative' }}>
          {/* Event details form */}
          <EventDetailsForm
            eventData={currentEvent || event}
            onEventUpdate={handleEventUpdate}
          />
          
          {/* Save button */}
          <Button
            variant="filled"
            mt="xl"
            fullWidth
            onClick={handleSave}
            disabled={isSaving}
          >
            Save Changes
          </Button>
        </Box>
      </Modal>
      
      {/* Event Display */}
        <Box px="md" pb="md">
          <Group wrap="nowrap" style={{flex: 1}}>
            <Box pos="relative">
              {/* Stacked photos effect with actual images */}
              {event.images && event.images.length > 2 && (
                <Box
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 4,
                    position: 'relative',
                    transform: 'rotate(-8deg)',
                    border: '1px solid #ced4da',
                    overflow: 'hidden',
                    zIndex: 1
                  }}
                >
                  <img
                    src={event.images[2].thumbnail_url || event.images[2].image_url || ""}
                    alt={"event preview background"}
                    style={{width: '100%', height: '100%', objectFit: 'cover'}}
                  />
                  <Box
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255,255,255,0.6)',
                    }}
                  />
                </Box>
              )}
              
              {event.images && event.images.length > 1 && (
                <Box
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 4,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    transform: 'rotate(-4deg)',
                    border: '1px solid #ced4da',
                    overflow: 'hidden',
                    zIndex: 2
                  }}
                >
                  <img
                    src={event.images[1].thumbnail_url || event.images[1].image_url || ""}
                    alt={"event preview middle"}
                    style={{width: '100%', height: '100%', objectFit: 'cover'}}
                  />
                  <Box
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(255,255,255,0.3)',
                    }}
                  />
                </Box>
              )}
              
              {/* Top image (always visible) */}
              <Box style={{
                width: 80,
                height: 80,
                overflow: 'hidden',
                borderRadius: 4,
                position: 'absolute',
                top: 0,
                left: 0,
                border: '1px solid #ced4da',
                zIndex: 3
              }}>
                {event.images && event.images.length > 0 ? (
                  <>
                    <img
                      src={event.images[0].thumbnail_url || event.images[0].image_url || ""}
                      alt={"event preview"}
                      style={{width: '100%', height: '100%', objectFit: 'cover'}}
                    />
                    {event.images.length > 3 && (
                      <Box
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0,0,0,0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Text c="white" fw={500}>+{event.images.length - 3}</Text>
                      </Box>
                    )}
                  </>
                ) : (
                  <Box
                    style={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#e9ecef',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <IconPhoto size={24} color="#adb5bd" />
                  </Box>
                )}
              </Box>
            </Box>
            <Stack gap={4}>
              <Text fw={500}>{event.title || 'Untitled Event'}</Text>
              <Text size="sm" c="dimmed">
                {event.date ? 
                  (event.date instanceof Date ? 
                    event.date.toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : 
                    new Date(event.date).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })
                  ) : 
                  'No date'
                }
              </Text>
              <Group gap={4}>
                <IconMapPin size={14} style={{color: '#868e96'}}/>
                <Text size="sm" c="dimmed">
                  {event.location_description || 
                   (event.location && typeof event.location === 'object' ? 
                     (event.location.address || event.location.city || 'Location available') : 
                     (typeof event.location === 'string' ? event.location : 'Unknown location'))}
                </Text>
              </Group>
            </Stack>
          </Group>
        </Box>
    </Paper>
  );
};
